import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { Tooltip } from "@msuite/ui/tooltip";
import React from "react";
import { useDecideIncomingInvoiceDuplicate } from "@/hooks/use-decide-incoming-invoice-duplicate";

/** Props Interface */
interface IncomingInvoiceDuplicateActionsProps {
	incomingInvoiceId: string;
	/** `labelled` beside a notice, `icons` in a table row. */
	variant?: "labelled" | "icons";
	/** Called once the duplicate is merged — the invoice is gone from every list then. */
	onMerged?: () => void;
}

/**
 * The two ways to settle a duplicate: merge it into its original, which takes a confirmation
 * because it cannot be undone, or declare it none.
 */
export function IncomingInvoiceDuplicateActions({
	incomingInvoiceId,
	variant = "labelled",
	onMerged,
}: IncomingInvoiceDuplicateActionsProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const confirmModal = useAsyncModal();
	const { decide, isDeciding } = useDecideIncomingInvoiceDuplicate();

	/** Functions */
	async function handleOnMerge() {
		if (!(await confirmModal.confirm())) return;
		await decide(incomingInvoiceId, "merge");
		onMerged?.();
	}

	async function handleOnDismiss() {
		await decide(incomingInvoiceId, "dismiss");
	}

	/** Computed */
	const isLabelled = variant === "labelled";

	/** Render */
	return (
		<React.Fragment>
			<Tooltip
				label={t("incoming_invoices.merge_duplicate")}
				isDisabled={isLabelled}
			>
				<Button
					size={isLabelled ? "sm" : "xxs"}
					variant={isLabelled ? "primary" : "ghost"}
					icon={<Icon name="Merge" />}
					onClick={handleOnMerge}
					isLoading={isDeciding}
					data-cy="merge-incoming-invoice-duplicate-button"
				>
					{isLabelled ? t("incoming_invoices.merge_duplicate") : undefined}
				</Button>
			</Tooltip>
			<Tooltip
				label={t("incoming_invoices.dismiss_duplicate")}
				isDisabled={isLabelled}
			>
				<Button
					size={isLabelled ? "sm" : "xxs"}
					variant={isLabelled ? "white" : "ghost"}
					icon={<Icon name="CopyX" />}
					onClick={handleOnDismiss}
					isLoading={isDeciding}
					data-cy="dismiss-incoming-invoice-duplicate-button"
				>
					{isLabelled ? t("incoming_invoices.dismiss_duplicate") : undefined}
				</Button>
			</Tooltip>
			{confirmModal.isOpen && (
				<ConfirmModal
					message={t("incoming_invoices.merge_duplicate_confirm_message")}
					controller={confirmModal}
				/>
			)}
		</React.Fragment>
	);
}
